import { useMemo } from 'react';

import { useMeQuery, usePeopleQuery } from '@/store/people/people.store';
import { VesselRole } from '@/store/people/people.types';
import type { SeatType } from '@/store/people/people.types';

import {
  SEAT_POOLS,
  byOwnerFirst,
  canAssignSeats,
  canOccupy,
  canVacate,
  isPaidSeat,
  seatCapacity,
  seatPeopleOf,
  seatTypeFor,
  seatsHeldForOwner,
  sitsIn,
} from './subscription.seats';
import type { SeatPerson, SeatPool } from './subscription.seats';
import type { ManagedVesselSubscription } from './useManagedVesselSubscriptions';

/** One seat pool, ready to render: who is in it, and who could be. */
export interface SeatPoolState {
  pool: SeatPool;
  /** `null` when the tier buys no such pool — a harbor vessel has no crew seats. */
  seatType: SeatType | null;
  capacity: number;
  /**
   * Seats within `capacity` that are paid for and deliberately empty: the management seat a
   * captain is holding for the owner they said they would add. Nobody can be put in one.
   */
  heldForOwner: number;
  occupants: SeatPerson[];
  /**
   * Eligible people holding no paid seat at all. The server would happily *move* someone out
   * of the other pool, but one person cannot sit in two seats, so offering that as an addition
   * would quietly empty a seat elsewhere — they have to be freed from the seat they hold first.
   */
  candidates: SeatPerson[];
  /** Whether to offer the add button: a free seat, someone to put in it, and the right to. */
  canAdd: boolean;
  /**
   * Whether the viewer may change who sits in this pool — empty a seat, move someone in or out:
   * the right to, and a pool that sells a paid seat. Freeing one resets it to `harbor_seat`,
   * which on the harbor tier is where it already is. Per-occupant, `canVacate` still has the
   * last word: neither the owner's seat nor the viewer's own is a target.
   */
  canManage: boolean;
  /** Who is looking, so `canVacate` can keep them from vacating themselves. */
  viewerPersonId: string | undefined;
}

export interface VesselSeats {
  pools: SeatPoolState[];
  /** Everyone on the vessel who could sit in a pool but sits in none. */
  unseated: SeatPerson[];
  /** Guests sitting in none of the pools — kept apart, since they never can. */
  guests: SeatPerson[];
  occupied: number;
  capacity: number;
  isLoading: boolean;
  error: unknown;
}

/**
 * Whether a person may be dragged from one pool into the other, or from no seat (`from` left
 * out) into one. The viewer has to manage the pool they land in, and the seat they leave, if
 * any, has to be one the viewer could empty — the same gate as its menu. The pool they land in
 * has to be one they are eligible for. Room is not checked here: a full pool can still take
 * them once a seat is bought (see `seatOffer`). `PATCH /roles/seat` moves them in one call, so
 * nothing has to be freed first.
 */
export const canMove = (
  person: SeatPerson,
  from: SeatPoolState | undefined,
  to: SeatPoolState,
): boolean =>
  from?.pool !== to.pool &&
  to.canManage &&
  (!from || (from.canManage && canVacate(person, from.viewerPersonId))) &&
  canOccupy(person.role, to.pool);

/** A seat held for the owner is not free — the server counts it too. */
export const hasFreeSeat = ({
  occupants,
  capacity,
  heldForOwner,
}: Pick<SeatPoolState, 'occupants' | 'capacity' | 'heldForOwner'>) =>
  occupants.length + heldForOwner < capacity;

const poolState = (
  pool: SeatPool,
  people: SeatPerson[],
  item: ManagedVesselSubscription,
  mayAssign: boolean,
  viewerPersonId: string | undefined,
): SeatPoolState => {
  const seatType = seatTypeFor(item.subscription?.tier ?? null, pool);
  const capacity = seatCapacity(item.subscription, pool);
  const heldForOwner = seatsHeldForOwner(item, pool);

  const occupants = seatType
    ? people.filter((person) => sitsIn(person.seatType, seatType, pool)).sort(byOwnerFirst)
    : [];
  const candidates = seatType
    ? people.filter((person) => !isPaidSeat(person.seatType) && canOccupy(person.role, pool))
    : [];

  return {
    pool,
    seatType,
    capacity,
    heldForOwner,
    occupants,
    candidates,
    viewerPersonId,
    // A held seat is not free: it is counted against capacity here for the same reason the
    // server counts it, so the last seat of a pool is not offered to somebody it is not for.
    canAdd:
      mayAssign &&
      !!seatType &&
      hasFreeSeat({ occupants, capacity, heldForOwner }) &&
      candidates.length > 0,
    canManage: mayAssign && isPaidSeat(seatType),
  };
};

/**
 * The vessel's seats, joined from its subscription (how many seats are paid for) and its
 * people (who occupies them).
 *
 * Occupancy comes from `GET /?service_id=…`, whose `PersonResponse.services[]` carries both
 * the person's `role` and their `seat_type` — the one call that reports both. The vessel is
 * its own service, so its id is the `service_id`.
 */
export const useVesselSeats = (item: ManagedVesselSubscription | null | undefined): VesselSeats => {
  const serviceId = item?.vessel.id;

  const { data: me } = useMeQuery();
  const peopleQuery = usePeopleQuery({ serviceId: serviceId! });

  return useMemo(() => {
    if (!item || !serviceId) {
      return {
        pools: [],
        unseated: [],
        guests: [],
        occupied: 0,
        capacity: 0,
        isLoading: false,
        error: null,
      };
    }

    const people = seatPeopleOf(peopleQuery.data ?? [], serviceId);
    const mayAssign = canAssignSeats(me, serviceId);
    const pools = SEAT_POOLS.map((pool) => poolState(pool, people, item, mayAssign, me?.id));
    const unseated = people.filter(
      (person) => !pools.some(({ occupants }) => occupants.includes(person)),
    );
    const isGuest = (person: SeatPerson) => person.role === VesselRole.GUEST;

    return {
      pools,
      unseated: unseated.filter((person) => !isGuest(person)),
      guests: unseated.filter(isGuest),
      occupied: pools.reduce((total, { occupants }) => total + occupants.length, 0),
      capacity: pools.reduce((total, { capacity }) => total + capacity, 0),
      isLoading: peopleQuery.isPending,
      error: peopleQuery.error,
    };
  }, [item, serviceId, me, peopleQuery.data, peopleQuery.isPending, peopleQuery.error]);
};
